// Iteration 088 -- Public sponsor offers page
import { useEffect, useMemo, useState } from "react";
import type { SponsorOfferPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ClaimOfferDialog } from "@/components/claim-offer-dialog";
import { useAuth } from "@/lib/auth-context";
import { Link } from "react-router-dom";

export default function OffersPage() {
  const { user } = useAuth();
  const [offers, setOffers] = useState<SponsorOfferPublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [sponsorFilter, setSponsorFilter] = useState<string | null>(null);
  const [claimTarget, setClaimTarget] = useState<SponsorOfferPublic | null>(null);

  useEffect(() => {
    setLoading(true);
    api
      .getPublicOffers()
      .then((r) => setOffers(r.offers))
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load offers"))
      .finally(() => setLoading(false));
  }, []);

  const sponsors = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of offers) m.set(o.sponsor_id, o.sponsor_name);
    return Array.from(m.entries());
  }, [offers]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return offers.filter((o) => {
      if (sponsorFilter && o.sponsor_id !== sponsorFilter) return false;
      if (!q) return true;
      return (
        o.title.toLowerCase().includes(q) ||
        o.description.toLowerCase().includes(q) ||
        o.sponsor_name.toLowerCase().includes(q)
      );
    });
  }, [offers, search, sponsorFilter]);

  function handleCtaClick(o: SponsorOfferPublic) {
    // Offer with external URL and no need for a formal claim → open external
    if (o.external_url && !o._actions.can_claim) {
      window.open(o.external_url, "_blank", "noopener,noreferrer");
      return;
    }
    if (!user) {
      // Not logged in → take them to login; offer id passed as next hint
      window.location.href = `/login?next=/offers`;
      return;
    }
    setClaimTarget(o);
  }

  if (loading) return <p className="p-6" data-page="offers" data-state="loading">Loading…</p>;

  return (
    <div className="mx-auto max-w-5xl p-4 space-y-4" data-page="offers">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Sponsor Offers</h1>
          <p className="text-sm text-muted-foreground">Exclusive deals from our sponsors.</p>
        </div>
        {!user && (
          <Link to="/login" className={buttonVariants({ variant: "outline", size: "sm" })}>
            Sign in
          </Link>
        )}
      </div>

      {error && <p className="text-sm text-destructive" data-error>{error}</p>}

      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Search offers…"
          className="max-w-sm"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="flex flex-wrap gap-1">
          <Button
            variant={sponsorFilter === null ? "default" : "outline"}
            size="sm"
            onClick={() => setSponsorFilter(null)}
          >
            All sponsors
          </Button>
          {sponsors.map(([id, name]) => (
            <Button
              key={id}
              variant={sponsorFilter === id ? "default" : "outline"}
              size="sm"
              onClick={() => setSponsorFilter(id)}
            >
              {name}
            </Button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground" data-state="empty">
            No offers match your filter.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((o) => (
            <Card key={o.id} data-item="offer" data-item-id={o.id}>
              {o.image_url && (
                <img
                  src={o.image_url}
                  alt=""
                  className="h-32 w-full rounded-t-md object-cover"
                />
              )}
              <CardHeader>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <CardTitle className="text-base">{o.title}</CardTitle>
                    <p className="text-xs text-muted-foreground">{o.sponsor_name}</p>
                  </div>
                  {o.discount_value && (
                    <span className="shrink-0 rounded bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                      {o.discount_value}
                    </span>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground line-clamp-3">{o.description}</p>
                {o.ends_at && (
                  <p className="text-[10px] uppercase text-muted-foreground">
                    Ends {new Date(o.ends_at).toLocaleDateString()}
                  </p>
                )}
                <Button className="w-full" onClick={() => handleCtaClick(o)}>
                  {o.call_to_action_label || "Claim Offer"}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {claimTarget && (
        <ClaimOfferDialog
          offer={claimTarget}
          onClose={() => setClaimTarget(null)}
          onClaimed={() => setClaimTarget(null)}
        />
      )}
    </div>
  );
}
